import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, ConfirmModal } from '../components'
import AddRFPModal from '../components/AddRFPModal'
import { authFetch } from '../lib/authFetch'

interface RFP {
  id: number
  rfp_code: string
  title: string
  platform_type: string
  status: string
  display_status?: string
  created_at: string
  current_step: number
  total_steps: number
}

// ── Status display map ──────────────────────────────────────────────────────
const STATUS_MAP: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  processing: {
    label: 'Processing',
    cls: 'bp-badge-running',
    icon: (
      <svg className="h-3 w-3 animate-spin-slow" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" d="M12 3a9 9 0 1 0 9 9" strokeOpacity={0.3} />
        <path strokeLinecap="round" d="M12 3a9 9 0 0 1 9 9" />
      </svg>
    ),
  },
  awaiting_input: {
    label: 'Awaiting Input',
    cls: 'bp-badge-waiting',
    icon: (
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M12 3a9 9 0 110 18 9 9 0 010-18z" />
      </svg>
    ),
  },
  review: {
    label: 'Waiting for Review',
    cls: 'bp-badge-waiting',
    icon: (
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  human_input_required: {
    label: 'Awaiting Input',
    cls: 'bp-badge-waiting',
    icon: (
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M12 3a9 9 0 110 18 9 9 0 010-18z" />
      </svg>
    ),
  },
  completed: {
    label: 'Completed',
    cls: 'bp-badge-done',
    icon: (
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
  archived: {
    label: 'Archived',
    cls: 'bp-badge-idle',
    icon: (
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
      </svg>
    ),
  },
  error: {
    label: 'Error',
    cls: 'bp-badge-blocked',
    icon: (
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M12 3a9 9 0 110 18 9 9 0 010-18z" />
      </svg>
    ),
  },
}

function getStatusDisplay(status: string) {
  return STATUS_MAP[status] || { label: status, cls: 'bp-badge-idle', icon: null }
}

type StatusGroup = 'active' | 'attention' | 'completed' | 'other'

const ATTENTION_STATUSES = new Set(['awaiting_input', 'review', 'human_input_required', 'error'])

function getStatusGroup(rfp: RFP): StatusGroup {
  const status = rfp.display_status || rfp.status
  if (ATTENTION_STATUSES.has(status)) return 'attention'
  if (status === 'processing') return 'active'
  if (status === 'completed') return 'completed'
  return 'other'
}

const FILTERS: { id: 'all' | StatusGroup; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'attention', label: 'Needs you' },
  { id: 'active', label: 'Running' },
  { id: 'completed', label: 'Done' },
]

type SortKey = 'newest' | 'oldest' | 'progress'

const PLATFORM_ACCENT: Record<string, string> = {
  Catalant: 'bg-blue-50 text-blue-700 ring-blue-600/15',
  Umbrex: 'bg-violet-50 text-violet-700 ring-violet-600/15',
}

function getRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

// ── Skeleton card ────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bp-card p-5">
      <div className="mb-4 flex items-start justify-between">
        <div className="h-5 w-24 bp-skeleton" />
        <div className="h-5 w-20 rounded-full bp-skeleton" />
      </div>
      <div className="mb-2 h-4 w-3/4 bp-skeleton" />
      <div className="mb-5 h-4 w-1/2 bp-skeleton" />
      <div className="mb-1 h-1.5 w-full rounded-full bp-skeleton" />
      <div className="mt-5 flex items-center justify-between">
        <div className="h-3 w-16 bp-skeleton" />
        <div className="h-8 w-20 rounded-xl bp-skeleton" />
      </div>
    </div>
  )
}

// ── RFP Card ─────────────────────────────────────────────────────────────────
function RFPCard({
  rfp,
  onOpen,
  onDelete,
}: {
  rfp: RFP
  onOpen: () => void
  onDelete: () => void
}) {
  const totalSteps = rfp.total_steps || 1
  const progress = Math.min(100, Math.max(0, (rfp.current_step / totalSteps) * 100))
  const { label, cls, icon } = getStatusDisplay(rfp.display_status || rfp.status)
  const platformCls = PLATFORM_ACCENT[rfp.platform_type] || 'bg-slate-50 text-slate-700 ring-slate-600/15'

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen()
      }}
      className="bp-card group flex cursor-pointer flex-col overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-card-hover focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-3 p-5 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {rfp.rfp_code || `RFP-${rfp.id}`}
          </span>
          <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${platformCls}`}>
            {rfp.platform_type}
          </span>
        </div>
        <span className={cls}>
          {icon}
          {label}
        </span>
      </div>

      <div className="px-5 pb-4">
        <h3 className="line-clamp-2 min-h-[2.6rem] text-[15px] font-semibold leading-snug text-slate-900 group-hover:text-primary-700">
          {rfp.title || `RFP ${rfp.id}`}
        </h3>
      </div>

      {/* Progress */}
      <div className="px-5 pb-5">
        <div className="mb-1.5 flex items-center justify-between text-xs text-slate-500">
          <span>
            Step <strong className="font-semibold text-slate-700">{rfp.current_step}</strong> of {totalSteps}
          </span>
          <span className="tabular-nums">{Math.round(progress)}%</span>
        </div>
        <div className="bp-progress-track">
          <div className="bp-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-5 py-2.5">
        <span className="flex items-center gap-1.5 text-xs text-slate-500" title={new Date(rfp.created_at).toLocaleString()}>
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {getRelativeTime(rfp.created_at)}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="bp-icon-btn h-8 w-8 opacity-70 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
            title="Delete RFP"
            aria-label="Delete RFP"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <span className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-primary-600 transition group-hover:bg-primary-50">
            Open
            <svg className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HomePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [rfps, setRfps] = useState<RFP[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | StatusGroup>('all')
  const [platformFilter, setPlatformFilter] = useState('all')
  const [sortKey, setSortKey] = useState<SortKey>('newest')
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null)
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => { fetchRFPs() }, [])

  useEffect(() => {
    const shouldOpen = Boolean((location.state as { openCreate?: boolean } | null)?.openCreate)
    if (!shouldOpen) return
    setShowAddModal(true)
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.pathname, location.state, navigate])

  // "/" focuses search, like most modern dashboards.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      if (e.key === '/' && !typing) {
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const fetchRFPs = async () => {
    try {
      setLoading(true)
      const response = await authFetch('/api/v1/rfps')
      if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`)
      const data = await response.json()
      setRfps(data.data || [])
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const handleRFPCreated = (rfpId: number, message?: string) => {
    setShowAddModal(false)
    showSuccess(message || `RFP #${rfpId} created. Pipeline is running in the background.`)
    fetchRFPs()
  }

  const showSuccess = (msg: string) => {
    setSuccess(msg)
    if (successTimer.current) clearTimeout(successTimer.current)
    successTimer.current = setTimeout(() => setSuccess(null), 6000)
  }

  const handleDeleteConfirmed = async () => {
    if (confirmDelete === null) return
    const rfpId = confirmDelete
    setConfirmDelete(null)
    try {
      const response = await authFetch(`/api/v1/rfps/${rfpId}`, {
        method: 'DELETE',
      })
      if (!response.ok) {
        const err = await response.json().catch(() => null)
        throw new Error(err?.detail || `Failed to delete: ${response.statusText}`)
      }
      await fetchRFPs()
    } catch (err) {
      setError((err as Error).message)
    }
  }

  // Everything the status pills do NOT control, so their counts describe the list
  // the user is actually looking at.
  const scopedRfps = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rfps.filter((r) => {
      if (platformFilter !== 'all' && r.platform_type !== platformFilter) return false
      return (
        !q ||
        (r.rfp_code || '').toLowerCase().includes(q) ||
        (r.title || '').toLowerCase().includes(q) ||
        r.platform_type.toLowerCase().includes(q)
      )
    })
  }, [rfps, platformFilter, search])

  const counts = useMemo(() => {
    const result = { all: scopedRfps.length, active: 0, attention: 0, completed: 0, other: 0 }
    for (const r of scopedRfps) result[getStatusGroup(r)] += 1
    return result
  }, [scopedRfps])

  const platforms = useMemo(
    () => Array.from(new Set(rfps.map((r) => r.platform_type).filter(Boolean))).sort(),
    [rfps]
  )

  const filtered = useMemo(() => {
    const list = scopedRfps.filter((r) => filter === 'all' || getStatusGroup(r) === filter)
    const time = (r: RFP) => new Date(r.created_at).getTime() || 0
    const pct = (r: RFP) => r.current_step / (r.total_steps || 1)
    return [...list].sort((a, b) => {
      if (sortKey === 'oldest') return time(a) - time(b)
      if (sortKey === 'progress') return pct(b) - pct(a) || time(b) - time(a)
      return time(b) - time(a)
    })
  }, [scopedRfps, filter, sortKey])

  const hasActiveFilters = Boolean(search) || filter !== 'all' || platformFilter !== 'all'
  const clearFilters = () => {
    setSearch('')
    setFilter('all')
    setPlatformFilter('all')
  }

  return (
    <div className="space-y-6">
      {/* Page heading */}
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Your RFPs</h1>

      {/* Notifications */}
      {error && (
        <div className="bp-alert-danger animate-slide-up" role="alert">
          <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700" aria-label="Dismiss">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      {success && (
        <div className="bp-alert-success animate-slide-up" role="status">
          <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-2 text-emerald-600 hover:text-emerald-800" aria-label="Dismiss">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Toolbar */}
      {!loading && rfps.length > 0 && (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="bp-segment self-start overflow-x-auto" role="tablist" aria-label="Filter by status">
            {FILTERS.map((f) => {
              const count = f.id === 'all' ? counts.all : counts[f.id]
              const active = filter === f.id
              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilter(f.id)}
                  className={`bp-segment-item ${active ? 'bp-segment-item-active' : ''}`}
                >
                  {f.label}
                  <span
                    className={`rounded-md px-1.5 text-[11px] font-semibold tabular-nums ${
                      active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              )
            })}
          </div>

          <div className="flex flex-1 flex-col gap-2 sm:flex-row lg:max-w-2xl lg:justify-end">
            <div className="relative flex-1">
              <svg className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title, code, or platform…"
                className="bp-input pl-10 pr-10"
                aria-label="Search RFPs"
              />
              <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-md border border-slate-200 bg-slate-50 px-1.5 text-[11px] font-medium text-slate-400 sm:block">
                /
              </kbd>
            </div>
            {platforms.length > 1 && (
              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="bp-select sm:w-44"
                aria-label="Filter by platform"
              >
                <option value="all">All platforms</option>
                {platforms.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            )}
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="bp-select sm:w-44"
              aria-label="Sort RFPs"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="progress">Most progress</option>
            </select>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        /* Empty state */
        <div className="bp-card flex flex-col items-center px-6 py-20 text-center animate-fade-in">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 ring-8 ring-primary-50/50">
            <svg className="h-8 w-8 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          {hasActiveFilters ? (
            <>
              <p className="text-lg font-semibold text-slate-800">
                {search ? `No results for "${search}"` : 'Nothing matches these filters'}
              </p>
              <p className="mt-1 text-sm text-slate-500">Try a different search term or filter.</p>
              <Button variant="secondary" onClick={clearFilters} className="mt-5">
                Clear filters
              </Button>
            </>
          ) : (
            <>
              <p className="text-lg font-semibold text-slate-800">No proposals yet</p>
              <p className="mt-1 max-w-sm text-sm text-slate-500">
                Create your first RFP to get started. The AI will guide you through the entire proposal process.
              </p>
              <Button onClick={() => setShowAddModal(true)} className="mt-6">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Create First RFP
              </Button>
            </>
          )}
        </div>
      ) : (
        /* Card grid */
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 animate-fade-in">
          {filtered.map((rfp) => (
            <RFPCard
              key={rfp.id}
              rfp={rfp}
              onOpen={() => navigate(`/rfp/${rfp.id}`)}
              onDelete={() => setConfirmDelete(rfp.id)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddRFPModal onClose={() => setShowAddModal(false)} onCreated={handleRFPCreated} />
      )}
      {confirmDelete !== null && (
        <ConfirmModal
          title="Delete this RFP?"
          message={`Are you sure you want to delete RFP #${confirmDelete}? This action cannot be undone and all associated data will be permanently removed.`}
          confirmLabel="Yes, Delete"
          cancelLabel="Keep it"
          variant="danger"
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}
